import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Badge,
  Input,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Skeleton,
  Label,
  useToast,
} from "@asm/ui";
import {
  Plus,
  Trash2,
  RefreshCw,
  Copy,
  Check,
  Globe,
  CheckCircle2,
  AlertTriangle,
  Shield,
  Play,
  Clock,
  XCircle,
  RotateCcw,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { domainsApi, type Domain } from "@/lib/api";
import { isValidDomain } from "@/lib/utils";

// Max attempts constant (should match backend)
const MAX_VERIFICATION_ATTEMPTS = 5;

export default function Domains() {
  const [newDomain, setNewDomain] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [domainToDelete, setDomainToDelete] = useState<string | null>(null);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState("");
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation("domains");
  const { t: tCommon } = useTranslation("common");

  const { data: domains, isLoading } = useQuery({
    queryKey: ["domains"],
    queryFn: () => domainsApi.list(),
  });

  const addDomain = useMutation({
    mutationFn: domainsApi.add,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["domains"] });
      setNewDomain("");
      setDialogOpen(false);
      toast({ title: t("dialog.success", "Domain added successfully") });
    },
  });

  const initiateVerification = useMutation({
    mutationFn: domainsApi.initiateVerification,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["domains"] });
      toast({ title: data.message || t("verification.startSuccess") });
    },
    onError: (error: Error) => {
      toast({ title: error.message || t("verification.failed"), variant: "destructive" });
    },
  });

  const retryVerification = useMutation({
    mutationFn: domainsApi.retryVerification,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["domains"] });
      toast({ title: data.message || t("verification.retrySuccess") });
    },
    onError: (error: Error) => {
      toast({ title: error.message || t("verification.failed"), variant: "destructive" });
    },
  });

  const verifyDomain = useMutation({
    mutationFn: domainsApi.checkVerification,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["domains"] });
      if (data.verified) {
        toast({ title: t("verification.success", "Domain verified successfully!") });
      }
    },
  });

  const deleteDomain = useMutation({
    mutationFn: domainsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["domains"] });
      // Reset delete dialog state
      setDeleteDialogOpen(false);
      setDomainToDelete(null);
      setDeleteConfirmInput("");
    },
  });

  // Handle opening delete confirmation dialog
  const openDeleteDialog = (domain: string) => {
    setDomainToDelete(domain);
    setDeleteConfirmInput("");
    setDeleteDialogOpen(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = () => {
    if (domainToDelete && deleteConfirmInput === domainToDelete) {
      deleteDomain.mutate(domainToDelete);
    }
  };

  // Check if delete is allowed (input matches domain name)
  const canDelete = domainToDelete && deleteConfirmInput === domainToDelete;

  const handleAddDomain = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDomain) return;

    // Validate domain syntax
    if (!isValidDomain(newDomain)) {
      setValidationError(t("dialog.invalidDomain"));
      return;
    }

    setValidationError(null);
    addDomain.mutate(newDomain.trim().toLowerCase());
  };

  const handleDomainChange = (value: string) => {
    setNewDomain(value);
    // Clear validation error when user starts typing
    if (validationError) {
      setValidationError(null);
    }
  };

  const copyToken = async (token: string) => {
    await navigator.clipboard.writeText(token);
    setCopiedToken(token);
    setTimeout(() => setCopiedToken(null), 2000);
  };

  // Calculate stats based on verification_status
  const verifiedCount = domains?.filter((d) => d.verification_status === "verified").length || 0;
  const pendingCount = domains?.filter((d) => d.verification_status === "pending").length || 0;
  const failedCount = domains?.filter((d) => d.verification_status === "failed").length || 0;
  const unverifiedCount = domains?.filter((d) => d.verification_status === "unverified").length || 0;

  // Get status badge based on verification_status
  const getStatusBadge = (domain: Domain) => {
    switch (domain.verification_status) {
      case "verified":
        return (
          <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            {t("status.verified")}
          </Badge>
        );
      case "pending":
        return (
          <div className="flex items-center gap-2">
            <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
              <Clock className="h-3 w-3 mr-1 animate-pulse" />
              {t("status.pending")}
            </Badge>
            <span className="text-xs text-slate-500">
              {t("verification.attempts", {
                current: domain.verification_attempts,
                max: MAX_VERIFICATION_ATTEMPTS,
              })}
            </span>
          </div>
        );
      case "failed":
        return (
          <div className="flex items-center gap-2">
            <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
              <XCircle className="h-3 w-3 mr-1" />
              {t("status.failed")}
            </Badge>
            <span className="text-xs text-slate-500">
              {t("verification.maxAttemptsReached")}
            </span>
          </div>
        );
      default: // unverified
        return (
          <Badge variant="outline" className="text-amber-400 border-amber-500/30">
            <AlertTriangle className="h-3 w-3 mr-1" />
            {t("status.unverified")}
          </Badge>
        );
    }
  };

  // Get icon based on verification_status
  const getStatusIcon = (domain: Domain) => {
    switch (domain.verification_status) {
      case "verified":
        return (
          <div className="p-2.5 rounded-lg border border-emerald-500/30 bg-emerald-500/10">
            <Shield className="h-5 w-5 text-emerald-400" />
          </div>
        );
      case "pending":
        return (
          <div className="p-2.5 rounded-lg border border-blue-500/30 bg-blue-500/10">
            <Clock className="h-5 w-5 text-blue-400" />
          </div>
        );
      case "failed":
        return (
          <div className="p-2.5 rounded-lg border border-red-500/30 bg-red-500/10">
            <XCircle className="h-5 w-5 text-red-400" />
          </div>
        );
      default:
        return (
          <div className="p-2.5 rounded-lg border border-amber-500/30 bg-amber-500/10">
            <AlertTriangle className="h-5 w-5 text-amber-400" />
          </div>
        );
    }
  };

  // Get action buttons based on verification_status
  const getActionButtons = (domain: Domain) => {
    const isInitiating = initiateVerification.isPending;
    const isRetrying = retryVerification.isPending;
    const isChecking = verifyDomain.isPending;

    switch (domain.verification_status) {
      case "unverified":
        return (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => initiateVerification.mutate(domain.domain)}
              disabled={isInitiating}
              className="text-cyan-400 border-cyan-500/30 hover:bg-cyan-500/10"
            >
              {isInitiating ? (
                <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Play className="h-4 w-4 mr-1" />
              )}
              {isInitiating ? t("verification.starting") : t("verification.startButton")}
            </Button>
          </>
        );
      case "pending":
        return (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              {t("verification.attempts", {
                current: domain.verification_attempts,
                max: MAX_VERIFICATION_ATTEMPTS,
              })}
            </span>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => verifyDomain.mutate(domain.domain)}
              disabled={isChecking}
              title={t("verification.checkButton")}
              className="h-8 w-8"
            >
              <RefreshCw className={`h-4 w-4 ${isChecking ? "animate-spin" : ""}`} />
            </Button>
          </div>
        );
      case "failed":
        return (
          <Button
            variant="outline"
            size="sm"
            onClick={() => retryVerification.mutate(domain.domain)}
            disabled={isRetrying}
            className="text-amber-400 border-amber-500/30 hover:bg-amber-500/10"
          >
            {isRetrying ? (
              <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <RotateCcw className="h-4 w-4 mr-1" />
            )}
            {isRetrying ? t("verification.retrying") : t("verification.retryButton")}
          </Button>
        );
      default:
        return null;
    }
  };

  return (
    <div className="tactical-grid space-y-8">
      {/* Header */}
      <div className="animate-fade-in-up">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
              <Globe className="h-8 w-8 text-cyan-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">{t("title")}</h1>
              <p className="text-muted-foreground">{t("description")}</p>
            </div>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) {
              setValidationError(null);
              setNewDomain("");
            }
          }}>
            <DialogTrigger asChild>
              <Button className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold glow-cyan">
                <Plus className="mr-2 h-4 w-4" />
                {t("addDomain")}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border">
              <form onSubmit={handleAddDomain}>
                <DialogHeader>
                  <DialogTitle>{t("dialog.title")}</DialogTitle>
                  <DialogDescription>{t("dialog.description")}</DialogDescription>
                </DialogHeader>
                <div className="py-4">
                  {(addDomain.error || validationError) && (
                    <div className="p-3 mb-4 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                      {validationError
                        ? validationError
                        : addDomain.error instanceof Error
                          ? addDomain.error.message
                          : t("dialog.failed")}
                    </div>
                  )}
                  <Label htmlFor="domain" className="text-muted-foreground">
                    {t("dialog.domainLabel")}
                  </Label>
                  <Input
                    id="domain"
                    type="text"
                    placeholder={t("dialog.domainPlaceholder")}
                    value={newDomain}
                    onChange={(e) => handleDomainChange(e.target.value)}
                    className={`mt-2 bg-muted/50 border-border font-mono ${validationError ? "border-red-500/50" : ""}`}
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    {t("dialog.domainHint")}
                  </p>
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                  >
                    {tCommon("buttons.cancel")}
                  </Button>
                  <Button
                    type="submit"
                    disabled={!newDomain || addDomain.isPending}
                    className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold"
                  >
                    {addDomain.isPending ? t("dialog.submitting") : t("dialog.submit")}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      {!isLoading && domains && domains.length > 0 && (
        <div className="animate-fade-in-up stagger-1 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="rounded-lg border border-border bg-card/50 p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">{t("stats.total")}</p>
            <p className="text-2xl font-bold font-mono">{domains.length}</p>
          </div>
          <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-4">
            <p className="text-xs text-emerald-400 uppercase tracking-wider mb-1">{t("stats.verified")}</p>
            <p className="text-2xl font-bold font-mono text-emerald-400">{verifiedCount}</p>
          </div>
          <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 p-4">
            <p className="text-xs text-blue-400 uppercase tracking-wider mb-1">{t("stats.pending")}</p>
            <p className="text-2xl font-bold font-mono text-blue-400">{pendingCount}</p>
          </div>
          <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-4">
            <p className="text-xs text-amber-400 uppercase tracking-wider mb-1">{t("stats.unverified")}</p>
            <p className="text-2xl font-bold font-mono text-amber-400">{unverifiedCount + failedCount}</p>
          </div>
        </div>
      )}

      {/* Domains List */}
      <div className="animate-fade-in-up stagger-2 rounded-lg border border-border bg-card/50 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-6 w-1 rounded-full bg-cyan-500" />
          <div>
            <h2 className="text-lg font-semibold">{t("list.title")}</h2>
            <p className="text-xs text-muted-foreground">{t("list.description")}</p>
          </div>
        </div>

        {isLoading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-20 w-full rounded-lg" />
            ))}
          </div>
        ) : domains?.length ? (
          <div className="space-y-3">
            {domains.map((domain: Domain, index: number) => (
              <div
                key={domain.id}
                className={`
                  rounded-lg border border-border bg-muted/30 p-4
                  animate-fade-in-up stagger-${(index % 8) + 1}
                `}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {getStatusIcon(domain)}
                    <div>
                      <p className="font-mono text-sm font-medium">{domain.domain}</p>
                      <p className="text-xs text-muted-foreground">
                        Added {new Date(domain.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    {getStatusBadge(domain)}

                    <div className="flex items-center gap-2">
                      {getActionButtons(domain)}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openDeleteDialog(domain.domain)}
                        title={t("delete.buttonTitle")}
                        className="h-8 w-8 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Verification token for non-verified domains */}
                {domain.verification_status !== "verified" && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <p className="text-xs text-muted-foreground mb-2">{t("list.token")}:</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-xs bg-muted px-3 py-2 rounded-lg font-mono text-cyan-400 border border-border overflow-hidden text-ellipsis">
                        {domain.verification_token}
                      </code>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyToken(domain.verification_token)}
                        className="h-8 gap-1"
                      >
                        {copiedToken === domain.verification_token ? (
                          <>
                            <Check className="h-3 w-3" />
                            Copied
                          </>
                        ) : (
                          <>
                            <Copy className="h-3 w-3" />
                            Copy
                          </>
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      DNS TXT Record: <code className="bg-muted px-1 rounded">_asm-verify.{domain.domain}</code> = <code className="bg-muted px-1 rounded">asm-verify={domain.verification_token}</code>
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="p-4 rounded-xl bg-muted/30 border border-border mb-6 inline-block">
              <Globe className="h-12 w-12 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2">{t("list.empty")}</h3>
            <p className="text-muted-foreground text-sm max-w-sm mx-auto mb-6">
              Add your first domain to start scanning and monitoring your attack surface.
            </p>
            <Button
              onClick={() => setDialogOpen(true)}
              className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold glow-cyan"
            >
              <Plus className="mr-2 h-4 w-4" />
              {t("list.addFirst")}
            </Button>
          </div>
        )}
      </div>

      {/* How to Verify */}
      <div className="animate-fade-in-up stagger-3 rounded-lg border border-border bg-card/50 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-6 w-1 rounded-full bg-cyan-500" />
          <div>
            <h2 className="text-lg font-semibold">{t("howTo.title")}</h2>
            <p className="text-xs text-muted-foreground">{t("howTo.description")}</p>
          </div>
        </div>

        <div className="space-y-4">
          {[
            {
              step: 1,
              title: t("howTo.step1.title"),
              description: (
                <>
                  {t("howTo.step1.description")}{" "}
                  <code className="bg-muted px-2 py-0.5 rounded text-cyan-400 text-xs font-mono">
                    _asm-verify
                  </code>
                </>
              ),
            },
            {
              step: 2,
              title: t("howTo.step2.title"),
              description: t("howTo.step2.description"),
            },
            {
              step: 3,
              title: t("howTo.step3.title"),
              description: t("howTo.step3.description"),
            },
            {
              step: 4,
              title: t("howTo.automatic.title"),
              description: t("howTo.automatic.description"),
            },
          ].map((item) => (
            <div key={item.step} className="flex gap-4">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-cyan-500/20 text-cyan-400 text-sm font-bold flex-shrink-0">
                {item.step}
              </div>
              <div>
                <p className="font-medium text-sm">{item.title}</p>
                <p className="text-sm text-muted-foreground">{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteDialogOpen(false);
            setDomainToDelete(null);
            setDeleteConfirmInput("");
          }
        }}
      >
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-red-400">
              {t("delete.title")}
            </DialogTitle>
            <DialogDescription>
              {t("delete.description")}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="p-3 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">{t("delete.warning")}</p>
                <p className="text-muted-foreground mt-1">{t("delete.warningDetails")}</p>
              </div>
            </div>
            <div>
              <Label htmlFor="deleteConfirm" className="text-muted-foreground">
                {t("delete.confirmLabel", { domain: domainToDelete })}
              </Label>
              <Input
                id="deleteConfirm"
                type="text"
                placeholder={domainToDelete || ""}
                value={deleteConfirmInput}
                onChange={(e) => setDeleteConfirmInput(e.target.value)}
                className="mt-2 bg-muted/50 border-border font-mono"
                autoComplete="off"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleteDomain.isPending}
            >
              {tCommon("buttons.cancel")}
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={!canDelete || deleteDomain.isPending}
              className="bg-red-600 hover:bg-red-500"
            >
              {deleteDomain.isPending ? t("delete.deleting") : t("delete.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
